<template>
  <UContainer>
    <LazyPostSlider />
    <div
      class="border-b-[1px] pb-4 mb-4 mt-6 flex justify-between border-neutral-200 dark:border-neutral-800 flex-wrap gap-2"
    >
      <div class="flex gap-2 flex-wrap">
        <UButton
          v-for="tab in tabs"
          :key="tab.value"
          :color="tab.active ? 'error' : 'neutral'"
          :variant="tab.active ? 'subtle' : 'outline'"
          :name="tab.value"
          class="relative"
          @click="onChangeTab(tab.value)"
        >
          <UIcon :name="tab.icon" class="mr-1" />
          {{ LL[tab.label]() }}
        </UButton>
      </div>
      <UButton
        color="error"
        variant="soft"
        icon="i-heroicons-document-plus"
        to="/posts/create"
        class="hidden md:flex"
      >
        {{ LL.create_articles() }}
      </UButton>
    </div>

    <div class="grid grid-cols-12 gap-3">
      <div class="col-span-12 lg:col-span-9">
        <div v-if="!isLoading" class="flex flex-col">
          <PostCard v-for="post in posts" :key="post.id" :post="post" />
        </div>
        <div v-if="isLoading">
          <USkeleton
            v-for="i in 10"
            :key="i"
            class="h-[94px] rounded-lg mt-4 first:mt-0"
          />
        </div>
        <div
          v-if="!isLoading && posts.length !== 0"
          class="flex items-center justify-center mt-10"
        >
          <UPagination
            v-model:page="params.page"
            show-edges
            :sibling-count="1"
            :items-per-page="params.size"
            :total="totalItems"
          />
        </div>
        <LazyEmpty
          v-if="!isLoading && posts.length === 0"
          :description="LL.no_results()"
        />
      </div>
      <div class="col-span-12 lg:col-span-3">
        <div>
          <div class="flex items-center gap-2">
            <div class="p-2 bg-pink-100 dark:bg-pink-900 rounded">
              <SvgUse
                name="ranking"
                path="/svgs/home.svg"
                class="w-6 h-6 text-pink-500 dark:text-white"
              />
            </div>
            <h4 class="text-xl font-bold">{{ LL.ranking_board() }}</h4>
          </div>
          <USeparator class="mt-4" />
          <PostTopAuthor
            v-for="(author, index) in authors"
            :key="author.id"
            :author="author"
            :position="index"
          />
        </div>
        <div class="mt-6">
          <HomeBlocksTagsPopular :tags="tags" />
        </div>
      </div>
      <USeparator class="mt-4" />
    </div>
  </UContainer>
</template>

<script lang="ts" setup>
import { reactive, ref } from 'vue'
import { EPostTabs } from '@/constants/enum'
import {
  type TPost,
  type TTopAuthor,
  getPosts,
  getTopAuthors,
getTopicPopulars,
} from 'features/post'
import { typesafeI18n } from '~/locales/i18n-vue'
import { useUserStore } from '~/store'

definePageMeta({
  layout: 'page',
})

const { LL } = typesafeI18n()
const userStore = useUserStore()

const tabs = [
  { value: EPostTabs.New, label: 'new', icon: 'i-heroicons-newspaper', active: true },
  { value: EPostTabs.Following, label: 'following', icon: 'i-heroicons-user-group', active: false },
  { value: EPostTabs.Bookmark, label: 'bookmark', icon: 'i-heroicons-bookmark', active: false }
]

const isLoading = ref(true)
const params = reactive({
  page: 1,
  size: 20,
})
const totalItems = ref(0)
const posts = ref<TPost[]>([])
const authors = ref<TTopAuthor[]>([])

const tagPopulars = await getTopicPopulars()
const tags = computed(() => tagPopulars.data.value?.data)

const handleCurrentChange = () => {
  fetchData()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

useSeoMeta({
  title: LL.value.seo_posts_title(),
  ogTitle: LL.value.seo_posts_description(),
  ogUrl: "https://techmely.com/posts",
});

watch(params, () => {
  handleCurrentChange()
})

const onChangeTab = (value) => {
  if (value === 'new') {
    navigateTo(`/posts`)
  } else {
    userStore.checkIsLogged(LL.value.you_need_to_login_to_action(), async () => {
      navigateTo(`/posts/${value}`)
    })
  }
}

const fetchDataInit = async () => {
  isLoading.value = true


  const postsParams = {
    type: EPostTabs.New,
    page: params.page,
    size: params.size,
  }
  const [{ data: listPosts }, { data: listAuthors }] = await Promise.all([
    getPosts(postsParams),
    getTopAuthors(),
  ])
  if (listPosts?.value) {
    posts.value = listPosts.value.items
    totalItems.value = listPosts.value.total
  }
  if (listAuthors?.value) {
    authors.value = listAuthors.value.data
  }
  isLoading.value = false
}

const fetchData = async () => {
  isLoading.value = true
  const { data: res } = await getPosts({
    type: EPostTabs.New,
    page: params.page,
    size: params.size,
  })
  if (res?.value) {
    posts.value = res.value.items
    totalItems.value = res.value.total
  }
  isLoading.value = false
}

await fetchDataInit()
</script>
